import React, { useState } from 'react';
import { Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, Link, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';

interface TwoFactorVerifyProps {
    challengeId: string;
    onSuccess: () => void;
    onCancel: () => void;
}

const TwoFactorVerify: React.FC<TwoFactorVerifyProps> = ({ challengeId, onSuccess, onCancel }) => {
    const { completeTwoFactorLogin } = useAuth();
    const { t } = useTranslation(['auth', 'common']);
    const [code, setCode] = useState('');
    const [useBackupCode, setUseBackupCode] = useState(false);
    const [rememberDevice, setRememberDevice] = useState(true);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);
        try {
            const result = await completeTwoFactorLogin(challengeId, code, rememberDevice);
            if (result.success) {
                onSuccess();
            } else {
                setError(result.error || t('twoFactor.invalidCode'));
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" fontWeight={600} mb={2}>
                {t('twoFactorVerify.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
                {useBackupCode
                    ? t('twoFactorVerify.backupCodeHint')
                    : t('twoFactorVerify.authCodeHint')}
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <form onSubmit={handleSubmit}>
                <TextField
                    label={useBackupCode ? t('twoFactor.backupCode') : t('twoFactor.authenticationCode')}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    fullWidth
                    required
                    size="small"
                    sx={{ mb: 2 }}
                    autoFocus
                />
                <FormControlLabel
                    control={<Checkbox checked={rememberDevice} onChange={(e) => setRememberDevice(e.target.checked)} />}
                    label={t('twoFactor.rememberDevice')}
                    sx={{ mb: 2 }}
                />
                <Button type="submit" variant="contained" fullWidth disabled={isLoading} sx={{ mb: 2 }}>
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : t('twoFactorVerify.verify')}
                </Button>
            </form>

            <Box textAlign="center">
                <Link component="button" variant="body2" onClick={() => { setUseBackupCode(!useBackupCode); setCode(''); setError(''); }}>
                    {useBackupCode ? t('twoFactorVerify.useAuthCode') : t('twoFactorVerify.useBackupCode')}
                </Link>
            </Box>
            <Box textAlign="center" sx={{ mt: 1 }}>
                <Link component="button" variant="body2" onClick={onCancel}>
                    {t('twoFactorVerify.backToLogin')}
                </Link>
            </Box>
        </Box>
    );
};

export default TwoFactorVerify;
